import { useState } from "react";
import { Landmark, RefreshCw, Wallet } from "lucide-react";

import { SeloBeta } from "@/components/BadgeBeta";
import { CarregandoBloco, CarregandoLista } from "@/components/carregando";
import { Button } from "@/components/ui/button";

import Avisos from "./components/Avisos";
import CardsFluxo from "./components/CardsFluxo";
import ContasERecebimentos from "./components/ContasERecebimentos";
import FiltroFluxo from "./components/FiltroFluxo";
import GraficoFluxo from "./components/GraficoFluxo";
import Quebras from "./components/Quebras";
import SaldoContaDialog from "./components/SaldoContaDialog";
import TabelaFluxo from "./components/TabelaFluxo";
import useFluxoCaixa from "./hooks/useFluxoCaixa";
import { dataLonga, reais } from "./lib/formato";

function Totais({ dados }) {
  const t = dados?.periodo_totais;
  if (!t) return null;
  const item = (rotulo, v, cor) => (
    <div className="px-4 py-2.5"><p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{rotulo}</p><p className={`text-lg font-bold tabular-nums ${cor || ""}`}>{reais(v)}</p></div>
  );
  return (
    <div className="rounded-2xl border bg-card grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0" data-e2e="totais-periodo">
      {item("Saldo no início", dados.saldo_abertura)}
      {item("Entradas no período", t.entradas, "text-emerald-700 dark:text-emerald-400")}
      {item("Saídas no período", -t.saidas, "text-red-600 dark:text-red-400")}
      {item("Saldo no fim", t.saldo_final, t.saldo_final < 0 ? "text-red-600 dark:text-red-400" : "")}
    </div>
  );
}

/**
 * FINANCEIRO → Fluxo de Caixa (v1.5.784). Responde "vai ter dinheiro na conta?":
 * vendas entram quando a maquininha/iFood pagam (prazo da forma, sem a taxa),
 * despesas saem quando são pagas ou vencem. O cálculo é do backend (api/v1/fluxo_caixa).
 */
export default function FluxoCaixaPage() {
  const f = useFluxoCaixa();
  const [saldoAberto, setSaldoAberto] = useState(false);
  const base = f.dados?.saldo_base;
  // Carregamento com cards (25/10/2026): só na primeira carga (ainda sem dados)
  // os blocos de baixo piscam em cinza, em vez de "Nenhuma conta em aberto" e
  // "Nada a receber" antes da hora. Trocar o período mantém o que já está na tela.
  const primeiraCarga = f.carregando && !f.dados;
  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-7xl mx-auto" data-e2e="fluxo-caixa">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2"><Landmark className="w-6 h-6 text-primary" /><h1 className="text-2xl sm:text-3xl font-bold">Fluxo de Caixa</h1><SeloBeta /></div>
          <p className="text-muted-foreground">Quando o dinheiro entra e sai da conta — o que já aconteceu e o que vem por aí.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setSaldoAberto(true)} data-e2e="botao-saldo">
            <Wallet className="w-4 h-4 mr-2" />{base ? `Saldo em ${dataLonga(base.data)}: ${reais(base.valor)}` : "Saldo da conta"}
          </Button>
          <Button variant="ghost" size="icon" onClick={f.recarregar} title="Atualizar"><RefreshCw className={`w-4 h-4 ${f.carregando ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>
      <CardsFluxo dados={f.dados} carregando={f.carregando} />
      <Avisos dados={f.dados} />
      <FiltroFluxo f={f} />
      {primeiraCarga ? (
        <>
          <CarregandoBloco altura="h-[84px]" />
          <CarregandoBloco altura="h-[420px]" />
          <div className="grid gap-4 lg:grid-cols-2">
            <CarregandoLista linhas={5} avatar={false} colunas={1} />
            <CarregandoLista linhas={5} avatar={false} colunas={1} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <CarregandoBloco altura="h-56" />
            <CarregandoBloco altura="h-56" />
          </div>
        </>
      ) : (
        <>
          <Totais dados={f.dados} />
          <GraficoFluxo dados={f.dados} />
          <ContasERecebimentos dados={f.dados} aoMudar={f.recarregar} />
          <Quebras dados={f.dados} />
          <TabelaFluxo dados={f.dados} />
        </>
      )}
      {saldoAberto && <SaldoContaDialog aberto={saldoAberto} setAberto={setSaldoAberto} base={base} aoSalvar={f.recarregar} />}
    </div>
  );
}
